import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getContentByType, getTranslation, ContentDocument } from '../api/contentApi';

const ImpressumPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const [impressum, setImpressum] = useState<ContentDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getContentByType('impressum')
      .then((items) => {
        setImpressum(items.length > 0 ? items[0] : null);
      })
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) return <div className="container"><p>{t('common.loading')}</p></div>;
  if (error) return <div className="container"><div className="error">{error}</div></div>;

  if (!impressum) {
    return (
      <div className="container">
        <h1 className="text-3xl font-serif font-bold text-primary-800 mb-4">{t('nav.impressum')}</h1>
        <p className="text-gray-500">{t('common.error')}</p>
      </div>
    );
  }

  const content = getTranslation(impressum, currentLang);
  if (!content) {
    return (
      <div className="container">
        <h1 className="text-3xl font-serif font-bold text-primary-800 mb-4">{t('nav.impressum')}</h1>
        <p className="text-gray-500">{t('common.error')}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('nav.impressum')} — CLAIRMONT | Rechtsanwalt Marcel Küster</title>
        <html lang={currentLang} />
      </Helmet>

      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/impressum.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('nav.impressum')}</h1>
        </div>
      </section>

      <div className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-serif font-bold text-primary-800 mb-8">{t('nav.impressum')}</h1>

        <div className="space-y-4 text-gray-700">
          {content.companyName && (
            <div>
              <strong className="text-primary-800">{content.companyName}</strong>
            </div>
          )}
          {content.address && (
            <div className="whitespace-pre-line">{content.address}</div>
          )}
          {content.phone && (
            <div>
              <span className="font-medium">{currentLang === 'de' ? 'Telefon' : currentLang === 'fr' ? 'Téléphone' : 'Phone'}:</span>{' '}
              <a href={`tel:${content.phone}`} className="text-primary-500 hover:text-primary-700">{content.phone}</a>
            </div>
          )}
          {content.email && (
            <div>
              <span className="font-medium">{currentLang === 'de' ? 'E-Mail' : currentLang === 'fr' ? 'E-mail' : 'Email'}:</span>{' '}
              <a href={`mailto:${content.email}`} className="text-primary-500 hover:text-primary-700">{content.email}</a>
            </div>
          )}
          {content.registrationNumber && (
            <div>
              <span className="font-medium">{currentLang === 'de' ? 'Registernummer' : currentLang === 'fr' ? 'Numéro d\'enregistrement' : 'Registration Number'}:</span>{' '}
              {content.registrationNumber}
            </div>
          )}
        </div>

        {content.additionalHtml && (
          <div className="mt-8 prose max-w-none" dangerouslySetInnerHTML={{ __html: content.additionalHtml }} />
        )}

        {content.disclaimer && (
          <div id="disclaimer" className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-xl font-serif font-semibold text-primary-800 mb-4">
              {currentLang === 'de' ? 'Rechtlicher Hinweis' : currentLang === 'fr' ? 'Mention légale' : 'Legal Notice'}
            </h2>
            <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: content.disclaimer }} />
          </div>
        )}
      </div>
    </>
  );
};

export default ImpressumPage;
